/* ============================================================
   Signed-in, entitled, store hydrated: the sidebar plus whichever page the
   route names. Also the home of the two cross-page concerns — the lapsed-
   account notice the write guard raises, and the keyboard shortcut that
   focuses capture from anywhere (`n`, like Things' quick entry) — and of the
   ground itself: the paper grain and the warm glow that follows the pointer.
   ============================================================ */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useApi, useEntitlement, usePreferences, useStore } from '@habishine/client-core';

import { Dialog } from '../components/Dialog';
import { Button } from '../components/ui';
import { CAPTURE_INPUT_ID } from '../components/Capture';
import { PaywallPanel } from '../pages/PaywallPage';
import { addDigestListener, permissionState, registerForPush } from '../push';
import { EditorsProvider } from './Editors';
import { MenuSheet, TabBar, TopBar } from './MobileNav';
import { Palette } from './Palette';
import { Sidebar } from './Sidebar';
import { ToastProvider } from './Toast';

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/* The glow's position is two CSS variables on the frame, written at most
   once a frame from a passive listener — React never re-renders for it. */
function usePointerGlow(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    let x = 60, y = 20;
    const paint = () => { raf = 0; el.style.setProperty('--mx', `${x}%`); el.style.setProperty('--my', `${y}%`); };
    const onMove = (e: PointerEvent) => {
      x = (e.clientX / window.innerWidth) * 100;
      y = (e.clientY / window.innerHeight) * 100;
      if (!raf) raf = requestAnimationFrame(paint);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => { window.removeEventListener('pointermove', onMove); if (raf) cancelAnimationFrame(raf); };
  }, [ref]);
}

export function AppShell() {
  const { upgradeRequested, dismissUpgrade } = useEntitlement();
  const { resync } = useStore();
  const { remindersEnabled } = usePreferences();
  const api = useApi();
  const navigate = useNavigate();
  const [palette, setPalette] = useState(false);
  const [menu, setMenu] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const closeMenu = useCallback(() => setMenu(false), []);
  const closePaywall = useCallback(() => setPaywall(false), []);
  const frame = useRef<HTMLDivElement>(null);
  usePointerGlow(frame);

  /* A push arriving while this tab is open: the digest summarizes changes made
     elsewhere, so re-sync; a click lands on Today (the reminder's task is on
     it or overdue) and records the click-through, fire-and-forget. */
  useEffect(
    () =>
      addDigestListener({
        onReceived: () => void resync().catch(() => {}),
        onOpen: (data) => {
          void resync().catch(() => {});
          navigate('/today');
          if (data.sendId) void api.post(`/v1/notifications/${data.sendId}/opened`, { via: 'web' }).catch(() => {});
        },
      }),
    [api, navigate, resync],
  );

  /* The silent repair (docs/notification-onboarding.md): a browser that
     already granted permission re-POSTs its subscription every launch the
     account wants reminders, because the push service can rotate it and the
     server would never notice. Idempotent server-side. Keyed on the preference,
     which arrives with the sync, so it runs once it is known. */
  const repaired = useRef(false);
  useEffect(() => {
    if (repaired.current || !remindersEnabled || permissionState() !== 'granted') return;
    repaired.current = true;
    void registerForPush(api);
  }, [api, remindersEnabled]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K opens the palette from anywhere, typing or not
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((p) => !p);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (e.key === '/') {
        e.preventDefault();
        setPalette(true);
        return;
      }
      if (e.key === 'n') {
        const input = document.getElementById(CAPTURE_INPUT_ID) as HTMLInputElement | null;
        if (input) {
          e.preventDefault();
          input.focus();
        }
      }
      // single-key page jumps, the Things / Linear idiom (no modifier, never while typing)
      const page = { t: '/today', p: '/plan', r: '/review', h: '/habits', ',': '/settings' }[e.key];
      if (page) {
        e.preventDefault();
        navigate(page);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <ToastProvider>
    <EditorsProvider>
    <div ref={frame} className="grain relative flex h-full">
      <div className="glow" aria-hidden />
      <Sidebar />
      <main className="relative z-[1] min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <TopBar onMenu={() => setMenu(true)} />
        {upgradeRequested ? (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-nudge px-6 py-3 text-sm text-ink">
            <span>Your subscription has lapsed, so changes can't be saved. Everything you've built is still here to read.</span>
            <span className="flex items-center gap-2">
              <Button variant="primary" size="sm" onClick={() => setPaywall(true)}>Choose a plan</Button>
              <Button variant="ghost" size="sm" onClick={dismissUpgrade}>Dismiss</Button>
            </span>
          </div>
        ) : null}
        {/* room under the content for the tab bar on a phone, breathing space on a desktop */}
        <div className="pb-[calc(84px+env(safe-area-inset-bottom))] md:pb-28">
          <Outlet />
        </div>
      </main>
      <TabBar />
      {menu ? <MenuSheet onClose={closeMenu} /> : null}
      {palette ? <Palette onClose={() => setPalette(false)} /> : null}
      {paywall ? (
        <Dialog title="Choose a plan" onClose={closePaywall}>
          <PaywallPanel compact onPurchased={closePaywall} />
        </Dialog>
      ) : null}
    </div>
    </EditorsProvider>
    </ToastProvider>
  );
}
